import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Button } from "../atoms/Button";
import { captureException } from "../../config/sentry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console for debugging
    console.error("ErrorBoundary caught an error:", error);
    console.error("Error info:", errorInfo);
    
    // Capture error in Sentry with component stack
    captureException(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });
  }

  handleRefresh = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/dashboard";
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-danger-50 dark:bg-danger-900/20 rounded-full flex items-center justify-center mb-6">
              <ExclamationTriangleIcon className="w-8 h-8 text-danger-500" />
            </div>

            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
              Something went wrong
            </h1>

            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              An unexpected error occurred. Don't worry, your data is safe. You
              can try refreshing the page or go back to the dashboard.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-4 bg-neutral-100 dark:bg-neutral-700 rounded-lg text-left overflow-auto">
                <p className="text-sm font-mono text-danger-600 dark:text-danger-400 break-all">
                  {typeof this.state.error.message === "string"
                    ? this.state.error.message
                    : "An unexpected error occurred"}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="secondary" onClick={this.handleGoHome}>
                Go to Dashboard
              </Button>
              <Button variant="primary" onClick={this.handleRefresh}>
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
